import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import {
  Sparkles, Mic, MicOff, X, Loader2, ArrowRight, AlertTriangle, Lock,
  ServerCog, Check, Undo2, Pencil,
} from 'lucide-react';
import { interpret, SUGGESTIONS } from '../../ai/interpret';
import { applyToRows, describe, needsServer, scopeMessage, summarise, OPERATIONS } from '../../ai/operations';

// =============================================================================
// AI assist — say what you want done to this page.
//
// THE MODEL NEVER TOUCHES THE DATABASE. It turns a sentence into an operation
// object (src/ai/operations.js); this component applies that object to the rows
// already on screen and marks them dirty. The page's own Save button does the
// writing, through the validation and role checks it already has. There is no
// new write path, so there is nothing new to secure.
//
// FOUR THINGS THIS SCREEN EXISTS TO DO, in order of how much they matter:
//
//   1. SHOW THE INTERPRETATION BEFORE ANYTHING HAPPENS. This is where somebody
//      catches "fifty percent" heard as "fifteen percent". Voice input makes
//      that a when, not an if, so the reading is stated in words and numerals
//      and can be edited without starting again.
//
//   2. REFUSE TO SILENTLY DO PART OF THE JOB. Mantle's grids hold a page, not a
//      dataset — 100 rows by default. "Increase everything by 5%" against a
//      loaded grid would change a hundred lines out of twelve hundred and look
//      completely successful. So scope is checked first and the request is
//      handed to the server path rather than quietly truncated.
//
//   3. CHANGE THE SCREEN, NOT THE DATABASE. Nothing is committed. The user sees
//      the numbers move in the grid they have been working in all morning, and
//      walking away discards it.
//
//   4. REFUSE CLEANLY. An unrecognised request gets an honest "I can't", not an
//      improvised interpretation of somebody's budget.
// =============================================================================

const STEP = { PROMPT: 'prompt', THINKING: 'thinking', REVIEW: 'review', DONE: 'done' };

export default function AiAssist({
  context = {},          // { module, pageLabel, versionLabel, siteLabel, versionLocked }
  rows = [],
  totalCount = null,     // server-known matching rows; null when unfiltered
  hasMore = false,
  fields = [],           // [{ key, label, get(row, slot), set?, periods?(row) }]
  onApply,               // (changes, summary, operation) => void
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        disabled={disabled}
        title="Describe a change in your own words"
        className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-semibold
                   border border-violet-300 bg-violet-50 text-violet-700 hover:bg-violet-100
                   dark:border-violet-500/40 dark:bg-violet-500/10 dark:text-violet-300 dark:hover:bg-violet-500/20
                   disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
      >
        <Sparkles className="w-3.5 h-3.5" />
        Ask AI
      </button>
      {open && (
        <AiAssistModal
          context={context} rows={rows} totalCount={totalCount} hasMore={hasMore}
          fields={fields} onApply={onApply} onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function AiAssistModal({ context, rows, totalCount, hasMore, fields, onApply, onClose }) {
  const [step, setStep] = useState(STEP.PROMPT);
  const [prompt, setPrompt] = useState('');
  const [result, setResult] = useState(null);   // { operation } | { refusal }
  const [preview, setPreview] = useState(null); // { changes, summary }
  const [listening, setListening] = useState(false);
  const [voiceError, setVoiceError] = useState('');
  const recogRef = useRef(null);
  const inputRef = useRef(null);

  const loadedCount = rows.length;
  // Memoised because it feeds the interpret callback: a fresh object every
  // render would rebuild that callback on every keystroke in the prompt box.
  const scope = useMemo(
    () => ({ loadedCount, totalCount, hasMore }),
    [loadedCount, totalCount, hasMore]
  );

  useEffect(() => { inputRef.current?.focus(); }, []);
  useEffect(() => {
    const esc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', esc);
    return () => document.removeEventListener('keydown', esc);
  }, [onClose]);

  // ---- voice ---------------------------------------------------------------
  // The transcript lands in the SAME editable box as typing, and never runs on
  // its own. Speech recognition mishears numbers, and a misheard number in a
  // budget is the failure this whole screen is arranged to prevent.
  const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
  const voiceSupported = !!SpeechRecognition;

  const toggleVoice = useCallback(() => {
    if (!voiceSupported) return;
    if (listening) { recogRef.current?.stop(); setListening(false); return; }
    setVoiceError('');
    const r = new SpeechRecognition();
    r.lang = 'en-ZA';
    r.interimResults = true;
    r.continuous = false;
    r.onresult = (e) => {
      const said = Array.from(e.results).map((x) => x[0].transcript).join('');
      setPrompt(said);
    };
    r.onerror = (e) => {
      setVoiceError(e.error === 'not-allowed'
        ? 'Microphone access was refused.'
        : 'Could not hear anything — type it instead.');
      setListening(false);
    };
    r.onend = () => setListening(false);
    recogRef.current = r;
    r.start();
    setListening(true);
  }, [listening, voiceSupported, SpeechRecognition]);

  useEffect(() => () => recogRef.current?.stop(), []);

  // ---- interpret -----------------------------------------------------------
  const ask = useCallback(async () => {
    if (!prompt.trim()) return;
    recogRef.current?.stop();
    setStep(STEP.THINKING);
    const res = await interpret(prompt, { ...context, fields });
    setResult(res);

    if (res.operation && !needsServer(res.operation, scope)) {
      const { changes, error } = applyToRows(res.operation, rows, fields);
      setPreview(error ? { error } : { changes, summary: summarise(changes) });
    } else {
      setPreview(null);
    }
    setStep(STEP.REVIEW);
  }, [prompt, context, fields, rows, scope]);

  const confirm = useCallback(() => {
    if (!preview?.changes?.length) return;
    onApply?.(preview.changes, preview.summary, result.operation);
    setStep(STEP.DONE);
  }, [preview, onApply, result]);

  const restart = () => { setStep(STEP.PROMPT); setResult(null); setPreview(null); };

  const locked = !!context.versionLocked;
  const op = result?.operation;
  const serverSide = op ? needsServer(op, scope) : false;

  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center bg-slate-900/50 p-6 pt-24 font-sans"
         onMouseDown={onClose}>
      <div className="bg-white dark:bg-slate-800 w-full max-w-2xl rounded-xl shadow-2xl overflow-hidden flex flex-col max-h-[80vh]"
           onMouseDown={(e) => e.stopPropagation()}>

        <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200 dark:border-slate-700 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            <Sparkles className="w-4 h-4 text-violet-600 dark:text-violet-400 shrink-0" />
            <h3 className="m-0 text-sm font-bold text-slate-900 dark:text-slate-100 truncate">
              Ask AI — {context.pageLabel || 'this page'}
            </h3>
            <span className="text-[11px] text-slate-400 truncate">
              {context.versionLabel} · {context.siteLabel || 'All sites'}
            </span>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 shrink-0">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto p-5">
          {locked ? <LockedState version={context.versionLabel} />
            : step === STEP.PROMPT ? (
              <PromptState
                prompt={prompt} setPrompt={setPrompt} inputRef={inputRef}
                listening={listening} voiceSupported={voiceSupported} voiceError={voiceError}
                toggleVoice={toggleVoice} onSubmit={ask} scope={scope}
              />
            ) : step === STEP.THINKING ? (
              <div className="h-32 flex flex-col items-center justify-center gap-2">
                <Loader2 className="w-5 h-5 animate-spin text-violet-500" />
                <p className="m-0 text-xs text-slate-500 dark:text-slate-400">Working out what you mean…</p>
              </div>
            ) : step === STEP.DONE ? (
              <DoneState summary={preview?.summary} />
            ) : (
              <ReviewState
                result={result} op={op} context={context} scope={scope}
                serverSide={serverSide} preview={preview} prompt={prompt}
              />
            )}
        </div>

        <div className="flex items-center justify-between gap-2 px-5 py-3 border-t border-slate-200 dark:border-slate-700 shrink-0 bg-slate-50 dark:bg-slate-800/60">
          <p className="m-0 text-[10px] text-slate-400 max-w-sm">
            {/* Stated on the screen, not just in a doc. It is the answer to the
                first question every client's IT department asks. */}
            Your figures are never sent to the AI — only the sentence you type and the list of changes it may request.
          </p>
          <div className="flex items-center gap-2">
            {step === STEP.REVIEW && !locked && (
              <button onClick={restart}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg border border-slate-300 dark:border-slate-600 text-xs font-medium text-slate-600 dark:text-slate-300 hover:border-slate-400">
                <Pencil className="w-3.5 h-3.5" /> Reword
              </button>
            )}
            {step === STEP.PROMPT && !locked && (
              <button onClick={ask} disabled={!prompt.trim()}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700 disabled:opacity-40">
                Interpret <ArrowRight className="w-3.5 h-3.5" />
              </button>
            )}
            {step === STEP.REVIEW && preview?.changes?.length > 0 && !serverSide && (
              <button onClick={confirm}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-violet-600 text-white text-xs font-semibold hover:bg-violet-700">
                <Check className="w-3.5 h-3.5" /> Apply to the grid
              </button>
            )}
            {(step === STEP.DONE || locked) && (
              <button onClick={onClose}
                className="px-3 py-1.5 rounded-lg bg-slate-800 dark:bg-slate-600 text-white text-xs font-semibold">
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

/* ----------------------------------------------------------------- states --- */

const PromptState = ({ prompt, setPrompt, inputRef, listening, voiceSupported, voiceError, toggleVoice, onSubmit, scope }) => (
  <>
    <p className="m-0 mb-2 text-xs text-slate-500 dark:text-slate-400">
      Describe the change in your own words. Nothing is saved until you review it and press Apply.
    </p>

    <div className="relative">
      <textarea
        ref={inputRef}
        value={prompt}
        onChange={(e) => setPrompt(e.target.value)}
        onKeyDown={(e) => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) onSubmit(); }}
        rows={3}
        placeholder="e.g. take last year's opex and increase it by 5%"
        className="w-full px-3 py-2.5 pr-11 text-sm rounded-lg border border-slate-300 dark:border-slate-600
                   bg-white dark:bg-slate-700 text-slate-900 dark:text-slate-100 outline-none
                   focus:border-violet-400 resize-none"
      />
      {voiceSupported && (
        <button
          type="button" onClick={toggleVoice} title={listening ? 'Stop listening' : 'Dictate'}
          className={`absolute top-2 right-2 p-1.5 rounded-md transition-colors ${listening
            ? 'bg-red-100 text-red-600 dark:bg-red-500/20 dark:text-red-400 animate-pulse'
            : 'text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-600'}`}
        >
          {listening ? <MicOff className="w-4 h-4" /> : <Mic className="w-4 h-4" />}
        </button>
      )}
    </div>

    {listening && (
      <p className="m-0 mt-1.5 text-[11px] text-red-600 dark:text-red-400">
        Listening — what you say lands in the box above, where you can correct it before anything runs.
      </p>
    )}
    {voiceError && <p className="m-0 mt-1.5 text-[11px] text-amber-600 dark:text-amber-400">{voiceError}</p>}
    {!voiceSupported && (
      <p className="m-0 mt-1.5 text-[11px] text-slate-400">Dictation is not available in this browser.</p>
    )}

    <p className="m-0 mt-4 mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">Try</p>
    <div className="flex flex-wrap gap-1.5">
      {SUGGESTIONS.map((s) => (
        <button key={s} type="button" onClick={() => setPrompt(s)}
          className="px-2 py-1 rounded-md border border-slate-200 dark:border-slate-600 text-[11px] text-slate-600 dark:text-slate-300 hover:border-violet-300 hover:text-violet-700 dark:hover:text-violet-300">
          {s}
        </button>
      ))}
    </div>

    <p className="m-0 mt-4 text-[11px] text-slate-400">{scopeMessage(scope)}</p>
  </>
);

const ReviewState = ({ result, op, context, scope, serverSide, preview, prompt }) => {
  if (result?.refusal) {
    return (
      <div>
        <Heard prompt={prompt} />
        <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg border border-amber-300 bg-amber-50 dark:border-amber-500/40 dark:bg-amber-500/10">
          <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5 text-amber-600 dark:text-amber-400" />
          <div>
            <p className="m-0 text-xs font-bold text-amber-800 dark:text-amber-300">{result.refusal}</p>
            {result.detail && <p className="m-0 mt-1 text-[11px] text-amber-700 dark:text-amber-400/90">{result.detail}</p>}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <Heard prompt={prompt} />

      {/* The interpretation, in words and numerals. The whole point of the
          screen: this is where a misheard number gets caught. */}
      <p className="m-0 mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">I understood</p>
      <div className="px-3 py-2.5 rounded-lg border border-violet-200 bg-violet-50 dark:border-violet-500/30 dark:bg-violet-500/10">
        <p className="m-0 text-sm font-semibold text-violet-900 dark:text-violet-200">{describe(op, context)}</p>
        <p className="m-0 mt-1 text-[11px] text-violet-700/80 dark:text-violet-300/70">
          {OPERATIONS[op.op]?.label} · {result.confidence === 'medium' ? 'moderate confidence' : 'high confidence'}
        </p>
      </div>

      {result.note && (
        <p className="m-0 mt-2 text-[11px] text-amber-700 dark:text-amber-400">{result.note}</p>
      )}

      {serverSide ? (
        <ServerHandoff op={op} scope={scope} />
      ) : preview?.error ? (
        <p className="m-0 mt-3 text-xs text-red-600 dark:text-red-400">{preview.error}</p>
      ) : preview?.changes?.length === 0 ? (
        <p className="m-0 mt-3 text-xs text-slate-500 dark:text-slate-400">
          Nothing on this page matches that, so there is nothing to change.
        </p>
      ) : (
        <ChangePreview preview={preview} />
      )}
    </div>
  );
};

const Heard = ({ prompt }) => (
  <>
    <p className="m-0 mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">You said</p>
    <p className="m-0 mb-4 text-xs italic text-slate-600 dark:text-slate-300">“{prompt}”</p>
  </>
);

// The hand-off that stops the worst bug this feature can have: changing the
// hundred rows that happen to be loaded and reporting success.
const ServerHandoff = ({ op, scope }) => (
  <div className="mt-3 flex items-start gap-2 px-3 py-2.5 rounded-lg border border-blue-300 bg-blue-50 dark:border-blue-500/40 dark:bg-blue-500/10">
    <ServerCog className="w-4 h-4 shrink-0 mt-0.5 text-blue-600 dark:text-blue-400" />
    <div className="min-w-0">
      <p className="m-0 text-xs font-bold text-blue-800 dark:text-blue-300">
        This is bigger than what is on screen.
      </p>
      <p className="m-0 mt-1 text-[11px] text-blue-700 dark:text-blue-400/90">
        {scopeMessage(scope)}{' '}
        {OPERATIONS[op.op]?.clientSide
          ? 'Applying it here would change only the loaded lines and leave the rest untouched, which would look like it worked.'
          : `A ${OPERATIONS[op.op]?.label.toLowerCase()} needs data that is not on this page.`}
      </p>
      <button
        type="button"
        className="mt-2 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md bg-blue-600 text-white text-[11px] font-semibold hover:bg-blue-700"
      >
        Run it across the whole version <ArrowRight className="w-3 h-3" />
      </button>
      <p className="m-0 mt-1.5 text-[10px] text-blue-600/70 dark:text-blue-400/60">
        Not built yet — this is where the server-side path with its own diff preview and audit record goes.
      </p>
    </div>
  </div>
);

const ChangePreview = ({ preview }) => {
  const { changes, summary } = preview;
  const biggest = useMemo(
    () => [...changes].sort((a, b) => Math.abs(b.to - b.from) - Math.abs(a.to - a.from)).slice(0, 6),
    [changes]
  );
  const fmt = (n) => Number(n).toLocaleString(undefined, { maximumFractionDigits: 2 });

  return (
    <div className="mt-3">
      <p className="m-0 mb-1.5 text-[10px] font-bold uppercase tracking-wider text-slate-400">What will change</p>
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <Stat label="Rows" value={summary.rowCount.toLocaleString()} />
        <Stat label="Cells" value={summary.cellCount.toLocaleString()} />
        <Stat label="Net movement" value={`${summary.net >= 0 ? '+' : '−'}${fmt(Math.abs(summary.net))}`}
              tone={summary.net >= 0 ? 'up' : 'down'} />
      </div>
      <div className="rounded-lg border border-slate-200 dark:border-slate-700 overflow-hidden">
        {biggest.map((c, i) => (
          <div key={i} className="flex items-center justify-between gap-3 px-3 py-1.5 text-[11px] border-b last:border-b-0 border-slate-100 dark:border-slate-700/60">
            <span className="truncate text-slate-600 dark:text-slate-300">
              {c.row.gl_account || c.row.description || c.row.stock_code || 'Line'}
              {c.slot != null && <span className="text-slate-400"> · P{c.slot}</span>}
            </span>
            <span className="shrink-0 tabular-nums text-slate-500 dark:text-slate-400">
              {fmt(c.from)} <span className="text-slate-300 dark:text-slate-600">→</span>{' '}
              <span className="font-semibold text-slate-800 dark:text-slate-100">{fmt(c.to)}</span>
            </span>
          </div>
        ))}
      </div>
      {changes.length > biggest.length && (
        <p className="m-0 mt-1.5 text-[11px] text-slate-400">
          and {(changes.length - biggest.length).toLocaleString()} more.
        </p>
      )}
      <p className="m-0 mt-2 text-[11px] text-slate-500 dark:text-slate-400">
        Applying marks these cells as unsaved in the grid. Nothing reaches the database until you press Save,
        and leaving the page discards it.
      </p>
    </div>
  );
};

const Stat = ({ label, value, tone }) => (
  <span className={`inline-flex items-baseline gap-1.5 px-2 py-1 rounded-md border text-[11px] ${
    tone === 'up' ? 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300'
      : tone === 'down' ? 'border-red-200 bg-red-50 text-red-700 dark:border-red-500/30 dark:bg-red-500/10 dark:text-red-300'
        : 'border-slate-200 bg-slate-50 text-slate-600 dark:border-slate-600 dark:bg-slate-700/50 dark:text-slate-300'}`}>
    <span className="font-bold uppercase tracking-wider opacity-60 text-[9px]">{label}</span>
    <span className="font-bold tabular-nums">{value}</span>
  </span>
);

const DoneState = ({ summary }) => (
  <div className="py-6 text-center">
    <Check className="w-8 h-8 mx-auto text-emerald-500" />
    <p className="m-0 mt-2 text-sm font-bold text-slate-900 dark:text-slate-100">
      {summary?.cellCount.toLocaleString()} cells changed in the grid
    </p>
    <p className="m-0 mt-1 text-xs text-slate-500 dark:text-slate-400">
      They are marked unsaved. Review them, adjust anything by hand, then press Save.
    </p>
    <p className="m-0 mt-3 inline-flex items-center gap-1.5 text-[11px] text-slate-400">
      <Undo2 className="w-3.5 h-3.5" /> Discard on the grid undoes all of it.
    </p>
  </div>
);

const LockedState = ({ version }) => (
  <div className="py-6 text-center">
    <Lock className="w-8 h-8 mx-auto text-slate-400" />
    <p className="m-0 mt-2 text-sm font-bold text-slate-900 dark:text-slate-100">{version} is locked.</p>
    <p className="m-0 mt-1 text-xs text-slate-500 dark:text-slate-400">
      Nothing can change it — by hand or otherwise. Switch to a working version to make changes.
    </p>
  </div>
);
